import {
  ApiBuildGalGame,
  ApiDeleteGalGameDialog,
  ApiDeleteGalGameDialogAnimation,
  ApiDeleteGalGameDialogOption,
  ApiDeleteGalGameDialogPortrait,
  ApiDeleteGalGameMedia,
  ApiDeleteGalGameStoryNode,
  ApiGetGalGame,
  ApiGetGalGameDialog,
  ApiGetGalGameDialogItem,
  ApiGetGalGameDialogPageByCharacterId,
  ApiGetGalGameScore,
  ApiGetGalGameStoryNode,
  ApiPostGalGameDialog,
  ApiPostGalGameDialogAnimation,
  ApiPostGalGameDialogOption,
  ApiPostGalGameDialogPortrait,
  ApiPostGalGamePublish,
  ApiPostGalGameStoryNode,
  ApiPostGalGameStoryNodeClone,
  ApiPostGalGameStoryNodeSort,
  ApiPutGalGame,
  ApiPutGalGameDialog,
  ApiPutGalGameDialogAnimation,
  ApiPutGalGameDialogOption,
  ApiPutGalGameDialogPortrait,
  ApiPutGalGameDialogSort,
  ApiPutGalGameMediaSort,
  ApiPutGalGameStoryNode,
} from "@/apis/gal-game";
import { defineStore } from "pinia";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { useGalGameHome } from "./galGameHome";
import { StorageGet } from "@/tools/localStorage";
import { useMate } from "./mate";
import api from "@/modules/api";
import { useMoment } from "./moment";
import { useStoryBook } from "./story-book";
import i18n from "@/locales";
import { RouteLocationRaw, useRouter } from "vue-router";
import { useGalGameOrigin } from "@/use/common";

// const storyBookStore = useStoryBook();

export const GAL_GAME_URL_PREFIX = `${useGalGameOrigin()}/webgal/`;

export const useGalGameEditor = defineStore("gal-game-editor", {
  state: () => ({
    tabType: "base",
    scrollTopCache: 0,
    scrollTopDialogCache: 0,

    galGameId: 0,
    galGameUUID: "",
    showJumpSettingModal: false, // 跳转设置对话框
    showJumpListModal: false, // 选择跳转的对话/节点
    showSelectMateModal: false, // 选择参与Mate
    showPlayerEditModal: false, // 编辑玩家信息
    showImgUploadModal: false, // 上传图片

    mediaType: "img" as "img" | "video", // 上传类型 img: 图片 video: 视频
    mediaIdx: -1,
    uploadType: 0, // 上传类型 0: 玩家扮演 1: 游戏封面图 2: 截图
    activeMediaId: undefined as number | undefined,

    showInputModal: false, // 输入框
    modalInputType: "",
    modalInputContent: "", // 输入框内容

    showScoreSliderModal: false, // 评分
    modalScore: 0, // 评分

    showGenerate: false, // 生成弹框
    showGenerateLoadingModal: false, // 生成loading
    generateContent: "",
    isGenerating: false,
    generateTarget: "" as GalGameParams | "node",
    generateChecked: ["type"] as GalGameParams[],
    activeCharacterId: 0 as number | undefined, // 当前选中的角色ID
    showSelectCharacterToneModal: false, // 选择角色音色弹框

    //
    showBgmModal: false, // 背景音乐
    bgmType: 0, // 0: 背景音乐 1: 节点音乐
    currentBgmId: 0,

    showPublishModal: false, // 发布弹框

    isParent: false, // 是否是父节点
    activeNode: {} as GalGameNode, // 当前选中的节点
    activeDialog: {} as GalGameDialog, // 当前选中的对话
    activeDialogOption: {} as GalGameDialogOption, // 当前选中的对话选项
    lastDialogIdEditedPerformance: 0, // 上次进入演出编辑器的对话ID

    connectionType: "",

    tmpMedia: { id: 0, url: "" }, // 临时url

    saveLoading: false,

    isInitGalGame: false,
    isInitNode: false,
    isInitDialog: false,

    nodeMap: {} as Record<number, GalGameNode>,
    dialogMap: {} as Record<number, GalGameDialog>,
    optionMap: {} as Record<number, GalGameDialogOption>,

    nodes: [] as number[],
    dialogs: [] as number[],
    options: [] as number[],

    endingList: [] as number[],
    plotList: [] as number[],
    beginningNode: 0,

    form: {
      id: 0,
      name: "",
      description: "",
      coverMediaId: 0,
      bgmMediaId: 0,
      type: 1,
      collectionIdzz: [] as number[],
      characterzz: [] as GalGameCharacter[],
      player: {
        name: "",
        introduction: "",
        avatarMediaId: 0,
      },
    } as GalGame,

    characterIdMap: {} as Record<number, GalGameCharacter>,
    mediaMap: {} as Record<number, Media>,
    mediaList: [] as number[],

    dialogList: [] as number[],
    activeCheckedJumpDialogId: 0 as number | "node", // 当前选中的跳转对话ID
    activeCheckedJumpNodeId: 0 as number, // 当前选中的跳转节点ID
    dialogShowTab: 0,
    dialogShowList: [] as number[],

    activeVersion: {
      versionNumber: ["1", "0", "0"],
      description: "",
    },
    latestVersion: null as GalGameVersion | null,
    versionzz: [] as GalGameVersion[],

    showCloneNodeModal: false,
    activeCloneNodeId: 0,

    showCharacterAllDialogModal: false,
    characterDialogList: [] as GalGameDialog[],
    activeStoryNodeId: undefined as number | undefined,
    needReloadCharacterDialogList: false,

    // 数值设置
    scoreList: [] as GalGameScore[],

    // 条件
    showConditionModal: false,

    // dialog 数值设置
    showScoreSettingModal: false,

    // 合集相关
    selectCollectionIdList: [] as number[],
  }),
  actions: {
    async addNode(node: GalGameNode) {
      this.saveLoading = true;
      const { item } = await ApiPostGalGameStoryNode(this.galGameId, node.type);
      this.saveLoading = false;
      node.id = item.id;
      this.nodeMap[node.id!] = item;
      this.nodes.push(node.id!);
      if (node.type === 1) {
        this.beginningNode = node.id!;
      }
      if (node.type === 2) {
        this.plotList.push(node.id!);
      }
      if (node.type === 3) {
        this.endingList.push(node.id!);
      }
    },
    async deleteNode(node: GalGameNode) {
      const { id, type } = node;
      this.saveLoading = true;
      await ApiDeleteGalGameStoryNode(id!);
      this.saveLoading = false;
      delete this.nodeMap[id!];
      const index = this.nodes.indexOf(id!);
      this.nodes.splice(index, 1);
      if (type === 2) this.plotList.splice(this.plotList.indexOf(id!), 1);
      if (type === 3) this.endingList.splice(this.endingList.indexOf(id!), 1);
    },
    async cloneNode(targetNodeId: number) {
      const loading = showLoadingToast({
        message: i18n.global.t("Message.nodeCloning"),
      });
      try {
        const { item } = await ApiPostGalGameStoryNodeClone(
          this.activeCloneNodeId
        );
        this.nodeMap[item.id!] = item;
        this.nodes.push(item.id!);
        const { type, id } = this.nodeMap[targetNodeId];
        if (type === 1) this.plotList.unshift(item.id!);
        if (type === 2)
          this.plotList.splice(this.plotList.indexOf(id!) + 1, 0, item.id!);
        if (type === 3) {
          item.type = 3;
          await this.saveNode(item);
          this.endingList.splice(this.endingList.indexOf(id!) + 1, 0, item.id!);
        }

        await this.sortNode();
        loading.close();
        showSuccessToast(i18n.global.t("Message.nodeCloneSuccessfully"));
      } catch (e) {
        loading.close();
        showFailToast(i18n.global.t("Message.cloningFailed"));
      }
    },
    async sortNode() {
      const { beginningNode, plotList, endingList, galGameId } = this;
      this.saveLoading = true;
      this.nodes = [beginningNode, ...plotList, ...endingList];
      await ApiPostGalGameStoryNodeSort({
        galGameId,
        storyNodeIdzz: this.nodes,
      });
      this.saveLoading = false;
    },
    async sortDialog() {
      const { activeNode } = this;
      this.saveLoading = true;
      await ApiPutGalGameDialogSort({
        storyNodeId: activeNode.id!,
        dialogIdzz: [...this.dialogList],
      });
      this.saveLoading = false;
    },
    async sortMedia() {
      const { galGameId } = this;
      this.saveLoading = true;
      await ApiPutGalGameMediaSort({
        galGameId,
        mediaIdzz: [...this.mediaList],
      });
      this.saveLoading = false;
    },
    async initNode(galGameId: number) {
      if (this.isInitNode) return;
      this.isInitNode = true;
      this.galGameId = galGameId;
      const { itemzz } = await ApiGetGalGameStoryNode(galGameId);
      let hasBeginning = false;
      let hasPlot = false;
      let hasEnding = false;
      for (const item of itemzz) {
        this.nodeMap[item.id!] = item;
        this.nodes.push(item.id!);

        if (item.type === 1) {
          hasBeginning = true;
          this.beginningNode = item.id!;
        }
        if (item.type === 2) {
          hasPlot = true;
          this.plotList.push(item.id!);
        }
        if (item.type === 3) {
          hasEnding = true;
          this.endingList.push(item.id!);
        }
      }
      if (!hasBeginning) this.addNode({ type: 1, name: "" });
      if (!hasPlot) this.addNode({ type: 2, name: "" });
      if (!hasEnding) this.addNode({ type: 3, name: "" });
    },
    async initScore() {
      const { itemzz } = await ApiGetGalGameScore(this.galGameId);
      this.scoreList = itemzz;
    },
    async initGalGame(uuid: string) {
      if (this.isInitGalGame) return;
      this.isInitGalGame = true;
      const { item } = await ApiGetGalGame(uuid);
      const {
        id,
        name,
        type,
        description,
        coverMediaId,
        coverMedia,
        characterzz,
        bgmMediaId,
        bgmMedia,
        latestVersion,
        versionzz,
        collectionzz,
        mediazz,
      } = item;
      this.galGameId = id;
      this.galGameUUID = uuid;
      this.initScore();
      const form: GalGame = {
        id,
        type,
        name,
        description,
        coverMediaId,
        coverMedia,
        bgmMediaId,
        bgmMedia,
        characterzz: [] as GalGameCharacter[],
      } as GalGame;

      // 避免重复添加
      if (!useStoryBook().form.characterzz)
        useStoryBook().form.characterzz = [];

      for (const character of characterzz) {
        const {
          id,
          mateId,
          name,
          role,
          introduction,
          avatarMedia,
          avatarMediaId,
          mateToneId,
          isScoreEnabled,
        } = character;
        if (mateId)
          useStoryBook().form.characterzz!.push({ mateId } as StoryCharacter);
        this.characterIdMap[id] = {
          id,
          mateId,
          name,
          avatarMediaId,
          avatarMedia,
          role,
          isScoreEnabled,
          mateToneId,
        };
        if (role === 1 || role === 2) {
          // 主角
          form.characterzz.push({
            id,
            mateId,
            introduction,
            role,
            mateToneId,
            isScoreEnabled,
            isOpen: false,
          });
        } else if (role === 3) {
          // 玩家
          form.player = {
            id,
            name,
            introduction,
            avatarMediaId,
            avatarMedia,
            mateToneId,
            isScoreEnabled,
            role,
          };
        } else if (role === 4) {
          // 旁白
          form.narrator = {
            id,
            role,
            mateToneId,
          };
        }
      }

      this.latestVersion = latestVersion;
      this.versionzz = versionzz;
      mediazz.forEach((media: Media) => {
        this.mediaMap[media.id!] = media;
        this.mediaList.push(media.id!);
      });

      if (this.latestVersion) {
        let [a, b, c] = this.latestVersion.version.split(".");
        c = parseInt(c) + 1 + "";
        this.activeVersion.versionNumber = [a, b, c];
      } else {
        this.activeVersion.versionNumber = ["1", "0", "0"];
      }
      form.collectionIdzz = collectionzz.map((v: any) => v.id);
      this.form = form;
    },

    async resetGame(item: GalGame) {
      this.$reset();
      const loading = showLoadingToast({
        message: i18n.global.t("Message.loading"),
      });
      await Promise.all([
        this.initGalGame(item.uuid!),
        this.initNode(item.id!),
      ]);
      loading.close();
    },

    async resetNode() {
      this.nodes = [];
      this.beginningNode = 0;
      this.plotList = [];
      this.endingList = [];
      this.isInitNode = false;
      await this.initNode(this.galGameId);
    },

    async addDialog(role: GalGameCharacterRole, text?: string, idx?: number) {
      if (this.saveLoading)
        return showFailToast(i18n.global.t("Message.addingRetry"));
      this.saveLoading = true;
      const { id } = this.activeNode;
      let characterId = 0;
      const characterIdCache = StorageGet(
        `galGameRoleSaveCache-${this.galGameId}-${role}`
      );
      if (characterIdCache) characterId = Number(characterIdCache);
      else
        Object.values(this.characterIdMap).some((v) => {
          if (v.role === role) {
            characterId = v.id!;
            return true;
          }
        });
      if (!characterId) {
        this.saveLoading = false;
        return showFailToast(i18n.global.t("Message.addThisRoleFirst"));
      }
      try {
        const { sceneMediaId, mateToneId, portraitzz } =
          this.getDefaultDialog(characterId);
        const { item } = await ApiPostGalGameDialog({
          storyNodeId: id!,
          characterId,
          text,
          sceneMediaId,
          mateToneId,
        });
        this.dialogMap[item.id!] = item;
        if (portraitzz.length > 0)
          await this.setDialogPortrait(item.id!, portraitzz);
        item.isOpen = false;
        // 避免重复添加会话
        if (!this.dialogList.includes(item.id!)) {
          this.nodeMap[id!].dialogzzCount!++;
          if (idx === undefined) this.dialogList.push(item.id!);
          else {
            this.dialogList.splice(idx, 0, item.id!);
            await this.sortDialog();
          }
          if (this.dialogShowTab === 0 || this.dialogShowTab === role)
            this.dialogShowList.push(item.id!);
        }
      } catch (e) {
        console.log(e);
        showFailToast(
          i18n.global.t("Message.conversationAdditionFailed") + (e || "")
        );
      }
      this.saveLoading = false;
    },
    getDefaultDialog(characterId: number) {
      const prevDialog =
        this.lastDialogIdEditedPerformance &&
        this.dialogMap[this.lastDialogIdEditedPerformance]
          ? this.dialogMap[this.lastDialogIdEditedPerformance]
          : null;
      let sceneMediaId: number | null = prevDialog?.sceneMediaId ?? null;
      let portraitzz: Pick<GalGameDialogPortrait, "mediaId" | "mediaInfo">[] =
        prevDialog?.portraitzz ?? [];
      if (!sceneMediaId && !portraitzz.length) {
        Object.keys(this.dialogMap).some((key) => {
          const dialog = this.dialogMap[Number(key)];
          if (
            !sceneMediaId &&
            dialog.sceneMediaId &&
            !portraitzz.length &&
            dialog.portraitzz.length
          ) {
            if (dialog.sceneMediaId) sceneMediaId = dialog.sceneMediaId;
            if (dialog.portraitzz) portraitzz = dialog.portraitzz;
            if (sceneMediaId || portraitzz.length) return true;
          }
        });
      }
      portraitzz = portraitzz.map((v) => ({
        mediaId: v.mediaId,
        mediaInfo: v.mediaInfo,
      }));
      const { mateToneId } = this.characterIdMap[characterId];
      return { sceneMediaId, mateToneId, portraitzz };
    },

    deleteDialog(dialog: GalGameDialog) {
      showConfirmDialog({
        title: i18n.global.t("ConfirmModal.deleteDialogue"),
        message: i18n.global.t("ConfirmModal.deleteDialogueText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          this.saveLoading = true;
          await ApiDeleteGalGameDialog(dialog.id!);
          showSuccessToast(i18n.global.t("Message.deleteSuccessful"));
          this.saveLoading = false;
          this.nodeMap[dialog.storyNodeId!].dialogzzCount!--;
          const index = this.dialogList.indexOf(dialog.id!);
          this.dialogList.splice(index, 1);
          delete this.dialogMap[dialog.id!];
          if (
            this.dialogShowTab === 0 ||
            this.dialogShowTab === dialog.character.role
          ) {
            const idx = this.dialogShowList.indexOf(dialog.id!);
            this.dialogShowList.splice(idx, 1);
          }
        })
        .catch(() => {});
    },

    async initDialog(nodeId: number, force = false) {
      if (this.isInitDialog && !force) return;
      this.isInitDialog = true;
      const res = await ApiGetGalGameDialog(nodeId);
      this.dialogList = res.itemzz.map((dialog: GalGameDialog) => {
        dialog.optionzz.forEach((option) => {
          this.optionMap[option.id!] = option;
        });
        return (
          (this.dialogMap[dialog.id!] = { ...dialog, isOpen: false }),
          dialog.id!
        );
      });
      this.nodeMap[nodeId].dialogzzCount = this.dialogList.length;
      this.dialogShowList = [...this.dialogList];
    },

    async addDialogOption(dialogId: number) {
      this.saveLoading = true;
      const idx = this.dialogList.findIndex((v) => v === dialogId);
      if (idx === -1) return;
      this.dialogMap[this.dialogList[idx]].isOpen = true;
      const { item } = await ApiPostGalGameDialogOption({ dialogId, text: "" });
      this.saveLoading = false;
      this.optionMap[item.id!] = item;
      this.dialogMap[this.dialogList[idx]].optionzz.push(item);
    },

    async updateDialogImg(
      dialogId: number,
      param: Partial<
        Pick<
          GalGameDialog,
          "sceneMediaId" | "sceneMediaInfo" | "previewMediaId"
        >
      >
    ) {
      const dialog = this.dialogMap[dialogId];
      const { item } = await ApiPutGalGameDialog(dialogId, param);
      dialog.sceneMediaId = item.sceneMediaId;
      dialog.sceneMediaInfo = item.sceneMediaInfo;
      dialog.previewMediaId = item.previewMediaId;
      dialog.sceneMedia = item.sceneMedia;
      dialog.previewMedia = item.previewMedia;
    },

    async saveDialog(dialog?: GalGameDialog) {
      if (!dialog) dialog = this.activeDialog;
      this.saveLoading = true;
      const res = await ApiPutGalGameDialog(dialog.id!, dialog);
      this.saveLoading = false;
      return res;
    },

    getDialogImgUrl() {
      if (!this.activeDialog.id)
        this.activeDialog = StorageGet("activeDialog") as GalGameDialog;
      return this.activeDialog.sceneMedia?.url;
    },

    async deleteDialogOption(option: GalGameDialogOption) {
      showConfirmDialog({
        title: i18n.global.t("ConfirmModal.deleteOption"),
        message: i18n.global.t("ConfirmModal.deleteOptionText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          const { id } = option;
          this.saveLoading = true;
          await ApiDeleteGalGameDialogOption(id!);
          this.saveLoading = false;
          const idx = this.dialogList.findIndex((v) =>
            this.dialogMap[v].optionzz.some((vv) => vv.id === id)
          );
          if (idx === -1) return;
          const optionIdx = this.dialogMap[
            this.dialogList[idx]
          ].optionzz.findIndex((v) => v.id === id);
          this.dialogMap[this.dialogList[idx]].optionzz.splice(optionIdx, 1);
        })
        .catch(() => {});
    },

    async saveDialogOption(option?: GalGameDialogOption) {
      if (!option) option = this.activeDialogOption;
      this.saveLoading = true;
      const res = await ApiPutGalGameDialogOption(option.id!, option);
      this.saveLoading = false;
      return res;
    },

    filterOptionText(dialog: GalGameDialog) {
      const idx = this.dialogList.findIndex((item) => item === dialog.id);
      if (idx === -1) {
        const { type } = this.nodeMap[dialog.storyNodeId];
        if (type === 1) return `${i18n.global.t("game.beginning")}：`;
        if (type === 2) {
          const idx = this.plotList.findIndex(
            (item) => item === dialog.storyNodeId
          );
          return `${i18n.global.t("game.plotNode")}${idx + 1}：`;
        }
        if (type === 3) {
          const idx = this.endingList.findIndex(
            (item) => item === dialog.storyNodeId
          );
          return `${i18n.global.t("game.ending.title")}${idx + 1}：`;
        }
        return `${i18n.global.t("game.nodeId")}:` + dialog.storyNodeId;
      }
      return `#${idx + 1}`;
    },
    filterNextDialogText(dialog: GalGameDialog) {
      const { id, text } = dialog;
      return this.dialogMap[id!]?.text || text;
    },
    async saveBase(form?: GalGame) {
      this.saveLoading = true;
      if (!form || !form.id) form = this.form;
      const { item } = await ApiPutGalGame(form.id!, form);
      const home = useGalGameHome();
      const origin = home.gameMap[form.id!];
      if (origin) home.gameMap[form.id!] = { ...origin, ...form };
      this.saveLoading = false;
      return item;
    },

    async saveNode(node?: GalGameNode) {
      this.saveLoading = true;
      if (!node) node = this.activeNode;
      await ApiPutGalGameStoryNode(node.id!, node);
      this.saveLoading = false;
    },

    async publishGalGame(text: string, isMoment = true) {
      // 1. 更新发布状态
      const loadingToast = showLoadingToast({
        message: i18n.global.t("Message.publishingGame"),
      });
      const { galGameId, galGameUUID, activeVersion, form } = this;
      let { versionNumber, description } = activeVersion;
      let isNewGame = false;
      if (!description) {
        description = form.description;
        isNewGame = true;
      }
      await ApiBuildGalGame(galGameId);
      await ApiPostGalGamePublish(
        galGameId,
        versionNumber.join("."),
        description
      );
      let nextRoute: RouteLocationRaw = "/";
      if (isMoment) {
        // 2. 发Moment
        const mate = useMate();
        await api.createMoment({
          mateId: mate.currentMate!.uuid,
          galGameId,
          text,
          visibility: 1,
          status: 1,
        });
        const moment = useMoment();
        await moment.fetchMomentList(true, 0);
      } else {
        // 3. 更新 home
        nextRoute = {
          name: "galGameDetail",
          params: { uuid: galGameUUID },
        };
      }
      await useGalGameHome().getOrFetchGame(galGameUUID, true);
      if (isNewGame) useGalGameHome().initCreate();
      loadingToast.close();
      useRouter()?.replace(nextRoute);
    },
    async setDialogPortrait(
      dialogId: number,
      portraits: {
        id?: number;
        mediaId: number;
        mediaInfo: MediaTransform;
      }[]
    ) {
      const dialog = this.dialogMap[dialogId];
      const oldPortraitIds = dialog.portraitzz.map((v) => v.id!);
      const toAddList = portraits.filter((p) => !p.id);
      const toDeleteIds = oldPortraitIds.filter(
        (id) => !portraits.some((p) => p.id === id)
      );
      const toUpdateList = portraits.filter((p) => p.id);
      const promises: Promise<any>[] = [];
      for (const portrait of toAddList) {
        promises.push(
          ApiPostGalGameDialogPortrait({
            dialogId,
            mediaId: portrait.mediaId,
            mediaInfo: portrait.mediaInfo,
          })
        );
      }
      for (const portrait of toUpdateList) {
        promises.push(
          ApiPutGalGameDialogPortrait(portrait.id!, {
            mediaId: portrait.mediaId,
            mediaInfo: portrait.mediaInfo,
          })
        );
      }
      for (const id of toDeleteIds) {
        promises.push(ApiDeleteGalGameDialogPortrait(id));
      }
      await Promise.all(promises);
      const { item } = await ApiGetGalGameDialogItem(dialogId);
      dialog.portraitzz = item.portraitzz;
    },
    async setDialogAnimation(
      dialogId: number,
      animations: {
        id?: number;
        animationId: number;
        mediaInfo: MediaTransform;
      }[]
    ) {
      const dialog = this.dialogMap[dialogId];
      const oldAnimationIds = dialog.animationzz.map((v) => v.id!);
      const toAddList = animations.filter((p) => !p.id);
      const toDeleteIds = oldAnimationIds.filter(
        (id) => !animations.some((p) => p.id === id)
      );
      const toUpdateList = animations.filter((p) => p.id);
      const promises: Promise<any>[] = [];
      for (const animation of toAddList) {
        promises.push(
          ApiPostGalGameDialogAnimation({
            dialogId,
            animationId: animation.animationId!,
            mediaInfo: animation.mediaInfo,
          })
        );
      }
      for (const animation of toUpdateList) {
        promises.push(
          ApiPutGalGameDialogAnimation(animation.id!, {
            animationId: animation.animationId,
            mediaInfo: animation.mediaInfo,
          })
        );
      }
      for (const id of toDeleteIds) {
        promises.push(ApiDeleteGalGameDialogAnimation(id));
      }
      await Promise.all(promises);
      const { item } = await ApiGetGalGameDialogItem(dialogId);
      dialog.animationzz = item.animationzz;
    },
    async getOrFetchDialog(dialogId: number) {
      if (this.dialogMap[dialogId]) return this.dialogMap[dialogId];
      const { item } = await ApiGetGalGameDialogItem(dialogId);
      this.dialogMap[dialogId] = item;
      return item;
    },
    // type = 'characterzz' | 'description'
    clickAutoGenerate(type: GalGameParams, id?: number) {
      this.generateTarget = type;
      if (type === "characterzz") this.activeCharacterId = id!;
      this.generateChecked = ["type", type];
      this.showGenerate = true;
    },

    // game media
    // img upload
    clickOpenUploadModal(uploadType: number, mediaType = "img") {
      this.showImgUploadModal = true;
      this.uploadType = uploadType;
      this.mediaType = mediaType as "img" | "video";
    },

    deleteMedia(id: number) {
      showConfirmDialog({
        title: i18n.global.t("Message.delete"),
        message: i18n.global.t("ConfirmModal.sureDeleteText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          this.saveLoading = true;
          await ApiDeleteGalGameMedia(id);
          this.saveLoading = false;
          const index = this.mediaList.indexOf(id);
          this.mediaList.splice(index, 1);
          delete this.mediaMap[id];
        })
        .catch(() => {});
    },

    clickSelectCharacterTone(id: number) {
      this.activeCharacterId = id;
      this.showSelectCharacterToneModal = true;
    },

    async onUpdateCharacterTone(toneId?: number) {
      const character = this.characterIdMap[this.activeCharacterId!];
      if (character.role === 3) {
        // 玩家
        this.form.player.mateToneId = toneId;
      } else if (character.role === 4) {
        // 旁白
        this.form.narrator.mateToneId = toneId;
      } else {
        const character = this.form.characterzz.find(
          (v) => v.id === this.activeCharacterId
        );
        character!.mateToneId = toneId;
      }
      character!.mateToneId = toneId;
      this.showSelectCharacterToneModal = false;
      await this.saveBase();
    },

    async fetchCharacterAllDialog() {
      const { galGameId, activeCharacterId, activeStoryNodeId } = this;
      const _page = this.characterDialogList.length / 10 + 1;
      if (!Number.isInteger(_page)) return true;
      const { page } = await ApiGetGalGameDialogPageByCharacterId(
        {
          galGameId,
          storyNodeId: activeStoryNodeId,
          characterId: activeCharacterId,
        },
        { page: _page, perPage: 10 }
      );
      this.characterDialogList.push(...page.data);
      return page.data.length === 10;
    },

    getScoreTextByEx(expression: string) {
      const valueIdNameMap: Record<number, string> = {};
      for (const score of this.scoreList)
        valueIdNameMap[score.id!] = score.name;
      let text = expression.replace(
        /_(\d+)_(\d+)/g,
        (match, characterId, valueId) => {
          // 获取 characterName 和 valueName
          const valueName = valueIdNameMap[Number(valueId)];
          if (characterId === "0") return `${valueName}`;
          const characterName = this.characterIdMap[characterId].name;
          // 如果找到了名称，则替换；否则保持原样
          return `${characterName}.${valueName}`;
        }
      );

      // & -> and | -> or
      text = text
        .replace(/\&\&/g, ` ${i18n.global.t("and")} `)
        .replace(/\|\|/g, ` ${i18n.global.t("or")} `);

      return text;
    },
  },
});
